import { format } from 'date-fns';
import {
  ColumnMapping,
  convertExcelDate,
  ExcelProcessorConfig,
  HeaderConfig,
  ParsedExcelData,
  ValidationError
} from '@/lib/excel';

type ExcelCell = string | number | boolean | Date | null | undefined;
type ParsedSection = Record<string, ExcelCell[]>;
type UUIDSectionResponse = Record<string, Array<string | null | undefined>>;
type UUIDValidationResponse = Record<string, UUIDSectionResponse | undefined>;

const HEADERS = {
  UNIT_CODE: 'Mã đơn vị',
  DOC_NUMBER: 'Số c/từ*',
  DOC_DATE: 'Ngày chứng từ*',
  CURRENCY: 'Ngoại tệ*',
  EXCHANGE_RATE: 'Tỷ giá',
  ADDRESS: 'Địa chỉ',
  DEPOSITOR: 'Người nộp tiền',
  DESCRIPTION: 'Diễn giải',
  DEBIT_ACCOUNT: 'Tài khoản nợ*',
  BANK: 'Ngân hàng',
  CUSTOMER_CODE: 'Mã khách hàng*',
  CUSTOMER_NAME: 'Tên khách hàng',
  CREDIT_ACCOUNT: 'Tài khoản có',
  AMOUNT_NT: 'Tiền nt',
  AMOUNT: 'Tiền',
  DETAIL_DESC: 'Diễn giải chi tiết',
  DEPARTMENT: 'Bộ phận',
  PAYMENT_TERM: 'Đợt thanh toán'
};

const HEADER_CONFIG: HeaderConfig[] = [
  // Main fields
  { header: HEADERS.DOC_NUMBER, key: 'so_ct', type: 'main' },
  { header: HEADERS.DOC_DATE, key: 'ngay_ct', type: 'main' },
  { header: HEADERS.CURRENCY, key: 'ma_nt', type: 'main' },
  { header: HEADERS.EXCHANGE_RATE, key: 'ty_gia', type: 'main' },
  { header: HEADERS.ADDRESS, key: 'dia_chi', type: 'main' },
  { header: HEADERS.DEPOSITOR, key: 'ong_ba', type: 'main' },
  { header: HEADERS.DESCRIPTION, key: 'dien_giai', type: 'main' },
  { header: HEADERS.DEBIT_ACCOUNT, key: 'tk', type: 'main' },
  { header: HEADERS.BANK, key: 'tknh', type: 'main' },
  // Detail fields
  { header: HEADERS.CUSTOMER_CODE, key: 'ma_kh', type: 'detail' },
  { header: HEADERS.CUSTOMER_NAME, key: 'ten_doi_tuong', type: 'detail' },
  { header: HEADERS.CREDIT_ACCOUNT, key: 'tk_co', type: 'detail' },
  { header: HEADERS.AMOUNT_NT, key: 'tien_nt', type: 'detail' },
  { header: HEADERS.AMOUNT, key: 'tien', type: 'detail' },
  { header: HEADERS.DETAIL_DESC, key: 'dien_giai', type: 'detail' },
  { header: HEADERS.DEPARTMENT, key: 'ma_bp', type: 'detail' },
  { header: HEADERS.PAYMENT_TERM, key: 'ma_dtt', type: 'detail' }
];

const UUID_FIELDS = {
  main: ['ma_nt', 'tk', 'tknh'],
  detail: ['ma_kh', 'tk_co', 'ma_bp', 'ma_dtt']
};

const getFieldName = (key: string) => {
  const config = HEADER_CONFIG.find(c => c.key === key);
  return config?.header?.replace('*', '').trim() || key;
};

const parseNum = (val: ExcelCell, def = 0) => {
  if (val == null || String(val).trim() === '') return def;
  const n = parseFloat(String(val));
  return Number.isNaN(n) ? def : Math.round(n * 100) / 100;
};

const stringVal = (value: ExcelCell) => (value == null ? undefined : String(value));

const hasData = (item: Record<string, ExcelCell>, keys: string[]) =>
  keys.some(k => item[k] != null && item[k] !== '' && item[k] !== 0);

const getVal = (uuid: UUIDValidationResponse, parsed: ParsedSection, section: string, field: string, i: number) =>
  uuid?.[section]?.[field]?.[i] || parsed[field]?.[i] || undefined;

const UUIDPayload = (data: ParsedExcelData) => {
  const build = (section: ParsedSection, fields: string[]) =>
    Object.fromEntries(
      fields.map(f => {
        const arr = section[f] || [];
        const hasAnyData = arr.some(v => v != null && v !== '');
        if (!hasAnyData) return [f, []];
        return [f, arr.map(v => (v == null || v === '' ? '' : v))];
      })
    );

  return {
    main: build(data.main || {}, UUID_FIELDS.main),
    chi_tiet: build(data.detail || {}, UUID_FIELDS.detail)
  };
};

const ValidateUUID = (
  response: UUIDValidationResponse,
  columnMapping: Record<string, ColumnMapping>
): ValidationError[] => {
  if (!response) return [];
  const errors: ValidationError[] = [];

  const sectionMap = { main: 'main', chi_tiet: 'detail' } as const;

  Object.entries(sectionMap).forEach(([responseKey, configType]) => {
    const data = response[responseKey];
    if (!data) return;

    Object.entries(data).forEach(([field, values]) => {
      if (!Array.isArray(values)) return;
      const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === configType);

      values.forEach((val, i) => {
        if (val === null) {
          errors.push({
            row: i + 2,
            field,
            message: `${getFieldName(field)} không hợp lệ`,
            section: configType,
            columnIndex: mapping?.index
          });
        }
      });
    });
  });

  return errors;
};

const TransformToBodies = (
  dataRows: ExcelCell[][],
  parsedData: ParsedExcelData,
  uuidResponse: UUIDValidationResponse,
  context: { entityUnitUUID: string }
): ImportBody[] => {
  const { main = {}, detail = {} } = parsedData;
  const records = new Map<string, ImportBody>();
  const detailMap = new Map<string, ImportBody['chi_tiet_giay_bao_co_items']>();

  const detailKeys = HEADER_CONFIG.filter(c => c.type === 'detail').map(c => c.key);

  for (let i = 0; i < dataRows.length; i++) {
    const docNum = String(main.so_ct?.[i] ?? '').trim();
    if (!docNum) continue;

    if (!records.has(docNum)) {
      const docDate = convertExcelDate(main.ngay_ct?.[i]) || format(new Date(), 'yyyy-MM-dd');
      records.set(docNum, {
        unit_id: context.entityUnitUUID,
        ma_ngv: '2',
        dia_chi: main.dia_chi?.[i] || undefined,
        ong_ba: main.ong_ba?.[i] || undefined,
        dien_giai: main.dien_giai?.[i] || undefined,
        tk: stringVal(getVal(uuidResponse, main, 'main', 'tk', i)) || '',
        tknh: stringVal(getVal(uuidResponse, main, 'main', 'tknh', i)),
        so_ct: docNum,
        ngay_ct: docDate,
        ngay_lct: docDate,
        i_so_ct: 0,
        ma_nt: stringVal(getVal(uuidResponse, main, 'main', 'ma_nt', i)) || '',
        ty_gia: parseNum(main.ty_gia?.[i], 1),
        status: '5',
        t_tien_nt: 0,
        t_tien: 0,
        ma_kh: '',
        chi_tiet_giay_bao_co_items: []
      });
    }

    const details = detailMap.get(docNum) || [];
    const tien_nt = parseNum(detail.tien_nt?.[i]);
    const tien = parseNum(detail.tien?.[i]) || tien_nt;

    const detailItem = {
      line: details.length + 1,
      dien_giai: stringVal(detail.dien_giai?.[i] || main.dien_giai?.[i]),
      ma_kh: stringVal(getVal(uuidResponse, detail, 'chi_tiet', 'ma_kh', i)),
      tk_co: stringVal(getVal(uuidResponse, detail, 'chi_tiet', 'tk_co', i)),
      tien_nt,
      tien,
      ma_bp: stringVal(getVal(uuidResponse, detail, 'chi_tiet', 'ma_bp', i)),
      ma_dtt: stringVal(getVal(uuidResponse, detail, 'chi_tiet', 'ma_dtt', i))
    };

    if (hasData(detailItem, detailKeys)) {
      details.push(detailItem);
      detailMap.set(docNum, details);
    }
  }

  return Array.from(records.values()).map(record => {
    record.chi_tiet_giay_bao_co_items = detailMap.get(record.so_ct || '') || [];
    record.t_tien_nt =
      Math.round(record.chi_tiet_giay_bao_co_items.reduce((a, i) => a + (i.tien_nt || 0), 0) * 100) / 100;
    record.t_tien = Math.round(record.chi_tiet_giay_bao_co_items.reduce((a, i) => a + (i.tien || 0), 0) * 100) / 100;
    record.ma_kh = record.chi_tiet_giay_bao_co_items[0]?.ma_kh || '';
    return record;
  });
};

export interface ImportBody {
  unit_id: string;
  ma_ngv?: string;
  dia_chi?: string;
  ong_ba?: string;
  dien_giai?: string;
  tk: string;
  tknh?: string;
  so_ct?: string;
  ngay_ct?: string;
  ngay_lct?: string;
  i_so_ct?: number;
  ma_nt: string;
  ty_gia: number | string;
  status?: string;
  t_tien_nt: number;
  t_tien: number;
  ma_kh: string;
  chi_tiet_giay_bao_co_items: Array<{
    line?: number;
    dien_giai?: string | null;
    ma_kh?: string | null;
    tk_co?: string | null;
    tien_nt?: number;
    tien?: number;
    ma_bp?: string | null;
    ma_dtt?: string | null;
  }>;
}

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};
